<template>
  <div ref="targetEl">
    <StyledOptionButton
      :class="classes.button"
      :href="url"
      :meta="meta"
      :odds="odds"
      :disabled="disabled"
      :buttonId="buttonId"
      :name="meta ? '' : name"
      :suspended="suspended"
      :winnings="localisedWinnings || undefined"
      @click="handleClick"
    />
  </div>
</template>

<script lang="ts">
import {
  injectBrand,
  BrandContext
} from "@fresh8/ad-data-composition/src/brand";
import { PropType, defineComponent, computed, provide } from "vue";
import { Option } from "@fresh8/types/src/betData";
import StyledOptionButton from "../../styled/WinButton.vue";
import { formatOdds } from "@fresh8/ad-data-composition/src/lib/formatOdds";
import usePotentialWinnings from "@fresh8/ad-data-composition/src/usePotentialWinnings";
import { getTranslation } from "@fresh8/ad-data-composition/src/lang";
import { createUseStyles } from "@fresh8/ad-data-composition/src/theme";
import { injectCardNumber, useAnalytics } from "../../../hooks/useAnalytics";
import { AdTemplate } from "@fresh8/types/src";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";

const { useStyles } = createUseStyles({
  button: {
    flexDirection: "column",

    "& .name": {
      fontSize: "8px",
      height: "13px"
    },

    "& .button": {
      justifyContent: "space-around !important",
      alignItems: "center"
    },

    "& .button div": {
      margin: 0
    }
  }
});

export default defineComponent({
  name: "WinButton",
  components: {
    StyledOptionButton
  },
  props: {
    option: { type: Object as PropType<Option>, required: true },
    isDraw: { type: Boolean, required: false, default: false },
    stagger: {
      type: Number,
      default: 0
    }
  },
  setup(props) {
    provide("button-transition-stagger", props.stagger);
    const brand = injectBrand() as BrandContext;
    const adTemplate = computed(() => brand.adTemplate.value as AdTemplate);

    const buttonId = computed(() => adTemplate.value.market?.oddsButton);
    const cardNumber = injectCardNumber();
    const { handleClick } = useAnalytics(
      {
        optionId: props?.option?.id,
        marketId: props?.option?.marketID,
        cardNumber: cardNumber
      },
      "Market Insights Button"
    );

    const odds = computed(
      () =>
        (props.option &&
          formatOdds(
            props.option.odds,
            adTemplate.value.market.oddsButtonTemplate
          )) ||
        "-"
    );

    const { localisedWinnings } = usePotentialWinnings(
      props.option?.odds || [0, 0]
    );

    const name = computed(() => {
      if (props.isDraw) {
        return getTranslation(window.navigator.language.toLowerCase())
          .ButtonTexts.Draw;
      }
      return props.option?.name.micro;
    });

    const { classesRef: classes } = useStyles(computed(() => ({})));

    const meta = computed(() => {
      let text = "";

      if (props.option?.meta) {
        if (!["Over", "Under"].includes(props.option.name.micro)) {
          text = props.option?.name.micro;
        }

        return `${text} ${props.option?.meta}`.trim();
      }

      return props.option?.name.micro;
    });

    const { targetEl } = composePopoverForm("Market Button", [
      {
        type: "button",
        get: () => adTemplate.value.market.oddsButton,
        set: (value: string) => (adTemplate.value.market.oddsButton = value)
      }
    ]);

    return {
      targetEl,
      handleClick: () => {
        if (!props.option?.id || props.option?.suspended) return;

        handleClick();
      },
      classes,
      odds,
      localisedWinnings,
      name,
      meta,
      url: props.option?.betslipUrl,
      disabled: !props.option || props.option?.suspended,
      buttonId,
      suspended: props.option?.suspended
    };
  }
});
</script>
